import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";

const ACTION_COLOR = {
  create: "text-emerald-500",
  update: "text-amber-400",
  delete: "text-red-500",
};
const SENSITIVE_ACTIONS = new Set(["reset_password", "send_reset_link"]);
const colorFor = (action) => ACTION_COLOR[action] || (SENSITIVE_ACTIONS.has(action) ? "text-red-500" : action === "change_password" ? "text-amber-400" : "");

const PAGE_SIZE = 50;

export default function AuditLog() {
  const [data, setData] = useState({ items: [], total: 0, facets: { actions: [], entities: [], actors: [] } });
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [actor, setActor] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", PAGE_SIZE); params.set("offset", offset);
    if (q) params.set("q", q); if (action) params.set("action", action);
    if (entity) params.set("entity", entity); if (actor) params.set("actor_email", actor);
    try { const r = await api.get(`/admin/audit-log?${params}`); setData(r.data); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [offset, action, entity, actor]);
  useEffect(() => { const t = setTimeout(() => { setOffset(0); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-line */ }, [q]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <div className="eyebrow mb-2">System</div>
      <h1 className="font-display text-5xl uppercase mb-2">Audit log</h1>
      <p className="text-zinc-400 mt-2 text-sm mb-8">Every admin create/update/delete is recorded here.</p>

      <div className="flex flex-wrap gap-3 mb-4">
        <input placeholder="Search notes, entity IDs, actors…" value={q} onChange={e => setQ(e.target.value)}
               className="bg-black border border-white/10 px-3 py-2 text-sm min-w-[260px]" data-testid="audit-search"/>
        <select value={action} onChange={e => { setAction(e.target.value); setOffset(0); }} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="audit-filter-action">
          <option value="">All actions</option>{data.facets.actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={entity} onChange={e => { setEntity(e.target.value); setOffset(0); }} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="audit-filter-entity">
          <option value="">All entities</option>{data.facets.entities.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={actor} onChange={e => { setActor(e.target.value); setOffset(0); }} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="audit-filter-actor">
          <option value="">All actors</option>{data.facets.actors.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="ml-auto text-zinc-500 text-sm px-2 py-2">{data.total} entries</div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-xs">
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Entity</th>
              <th className="text-left px-4 py-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-16 text-zinc-500">Loading…</td></tr> : data.items.map(e => (
              <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{e.at?.replace("T", " ").slice(0,19)}</td>
                <td className="px-4 py-3">{e.actor_email || "—"}</td>
                <td className={`px-4 py-3 font-bold uppercase text-xs ${colorFor(e.action) || "text-zinc-300"}`}>{e.action}</td>
                <td className="px-4 py-3 text-zinc-300">{e.entity}</td>
                <td className="px-4 py-3 text-zinc-400 max-w-md truncate">{e.note}</td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && <tr><td colSpan={5} className="text-center py-16 text-zinc-500">No log entries match.</td></tr>}
          </tbody>
        </table>
      </div>

      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="btn-ghost !py-1 !px-3 !text-xs disabled:opacity-40" data-testid="audit-prev">Previous</button>
          <div className="text-zinc-500">Page {currentPage} of {totalPages}</div>
          <button disabled={offset + PAGE_SIZE >= data.total} onClick={() => setOffset(offset + PAGE_SIZE)}
            className="btn-ghost !py-1 !px-3 !text-xs disabled:opacity-40" data-testid="audit-next">Next</button>
        </div>
      )}
    </div>
  );
}
